import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Box, Flex } from '@theme-ui/components';
import Cookies from 'js-cookie';
import { useTheme } from '@emotion/react';

import type { Magento2_CategoryInterface } from '@app/__graphql__/types';
import Loader from '@app/components/Loader';
import NotFound from '@app/components/NotFound';
import { COUNTRY_COOKIE } from '@app/helpers/localization';
import { pushData, pushUserData } from '@app/helpers/tracking';
import useAuthentication from '@app/hooks/useAuthentication';
import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';
import GiftProductConfigurableLayout from '@app/pages/Pricing/components/GiftProductLayout/GiftProductConfigurableLayout';

import type { PricingPage_content_pricing_pages_layout_item_pricing_pages_single_layout as SingleLayout } from '../__generated__/PricingPage';
import ContentBlock from '../components/ContentBlock';
import PricingPageHeader from '../components/PricingPageHeader/PricingPageHeader';
import BundleProductLayout from '../components/ProductLayout/BundleProductLayout';
import ConfigurableProductLayout from '../components/ProductLayout/ConfigurableProductLayout';
import SupportedPaymentMethods from '../components/SupportedPaymentMethods';
import type {
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as Variant,
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants_product as VariantProduct,
} from '../hook/__generated__/PricingPageShopProduct';
import useShopProduct from '../hook/useShopProduct';
import AdditionalInfo from './components/AdditionalInfo';
import messages from './SingleProduct.messages';
import { BottomContent, ContentWrapper } from './styles';

export interface SingleProductLayoutProps {
  layout: SingleLayout;
}

/*@TODO [WWW-11705] Migrate NI to new graphql-codegen generator syntax.
 * For now we extend the type manually
 */
export interface VariantProductExtendedType extends VariantProduct {
  categories: Magento2_CategoryInterface[];
}

const SingleProductLayout = ({ layout }: SingleProductLayoutProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const { language } = useParams<{
    language: string;
  }>();
  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();

  const { show_retailer_button, content, product } = layout;
  const sku = product?.sku;
  const { loading, error, notFound, magentoProduct } = useShopProduct(sku);
  const { isAuthenticated, loading: isAuthenticatedLoading } =
    useAuthentication();
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const { system_requirements } = product ?? {};

  const isConfigurable =
    magentoProduct?.__typename === 'Magento2_ConfigurableProduct';

  const variants = useMemo(() => {
    return isConfigurable ? magentoProduct?.variants : [];
  }, [isConfigurable, magentoProduct]);

  const hasVariants = variants?.length;

  const niVariants = useMemo(
    () =>
      variants?.filter((variant) => {
        const productVariant = variant?.product as VariantProductExtendedType;

        return !productVariant?.categories?.find(
          (category) => category.name === 'NI Hidden',
        );
      }) ?? null,
    [variants],
  );

  useEffect(() => {
    if (isAuthenticatedLoading || !product || !language) return;

    pushUserData({
      isAuthenticated,
      country: Cookies.get(COUNTRY_COOKIE),
      language,
      pageCategory: 'www',
    });

    pushData({
      google_tag_params: {
        pagetype: 'product',
        pname: product?.title,
        prodid: product?.sku,
      },
    });
  }, [isAuthenticated, isAuthenticatedLoading, language, product]);

  const ProductLayoutWrapper = useCallback(() => {
    if (!product || !magentoProduct) return null;
    switch (magentoProduct?.__typename) {
      case 'Magento2_ConfigurableProduct':
        return (
          <ConfigurableProductLayout
            product={product}
            variants={niVariants}
            showRetailerButton={show_retailer_button}
            onSelectVariant={setSelectedVariant}
          />
        );

      case 'Magento2_BundleProduct':
        return (
          <BundleProductLayout
            product={product}
            magentoProduct={magentoProduct}
            showRetailerButton={show_retailer_button}
          />
        );

      case 'Magento2_GiftCardProduct':
        if (sku) {
          return <GiftProductConfigurableLayout product={product} sku={sku} />;
        }
        return null;

      default:
        return null;
    }
  }, [product, magentoProduct, show_retailer_button, sku, niVariants]);

  if (loading) {
    return (
      <Flex py={4} sx={{ justifyContent: 'center' }}>
        <Loader color={theme.colors.lighterBlue} size={30} />
      </Flex>
    );
  }

  if (error) {
    throw new Error(
      'Unable to query shop products in single pricing page layout',
    );
  }

  const hasMissingVariants = isConfigurable && !hasVariants;

  if (notFound || hasMissingVariants) {
    const errorMessage = notFound
      ? 'Product is not found'
      : !isConfigurable
        ? `Unsupported product type: ${magentoProduct?.__typename}`
        : 'Product has no magento variants';

    return <NotFound errorMessage={errorMessage} />;
  }

  const additional_information = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'additional_information',
  });

  const product_includes = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'product_includes',
  });

  const systemRequirements = system_requirements?.reduce((acc, sys) => {
    const translations = sys?.system_requirements_id?.translations;
    return (
      acc +
      findTranslationByKeyWithFallback({
        translations,
        key: 'content',
      })
    );
  }, '');

  const bottomContent = content?.[0]?.bottom_content;

  return (
    <Box data-testid="pricing_pages_single_layout">
      <Box mb={5}>
        <PricingPageHeader sku={sku} />
      </Box>
      <ContentWrapper px={4}>
        <ProductLayoutWrapper />
        <SupportedPaymentMethods />
        <AdditionalInfo
          contentInfo={additional_information}
          variantInfo={selectedVariant?.product?.additionalInfo}
        />

        {(product_includes || systemRequirements) && (
          <Flex sx={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
            {product_includes && (
              <ContentBlock
                title={intl.formatMessage(messages.productIncludesTitle)}
                innerHtml={product_includes}
                sx={{ width: ['100%', product_includes ? '48%' : '100%'] }}
              />
            )}
            {systemRequirements && (
              <ContentBlock
                data-testid="system-requirements"
                title={intl.formatMessage(messages.systemRequirementsTitle)}
                innerHtml={systemRequirements}
                sx={{ width: ['100%', product_includes ? '48%' : '100%'] }}
              />
            )}
          </Flex>
        )}

        {bottomContent && (
          <BottomContent dangerouslySetInnerHTML={{ __html: bottomContent }} />
        )}
      </ContentWrapper>
    </Box>
  );
};

export default SingleProductLayout;
